// Inline sanitize preset — @github-ui/sanitize's `inline` preset for constrained
// inline text (github/react-platform#2035, epic github/react-platform#1723).
//
// A port of memex's TEXT_SANITIZATION_CONFIG
// (packages/memex/src/client/helpers/sanitize.ts): a radically narrow tag set for
// content that is conceptually a single line of text — issue/PR titles, custom-field
// text, option/label names — rather than rendered block markdown. Only inline
// constructs survive; block-level markup is flattened (KEEP_CONTENT keeps the text).
//
// DEVIATION FROM MEMEX TEXT — inert inline emphasis. Beyond memex's baseline this
// preset also keeps `strong` / `em` / `b` / `i`: purely presentational tags that
// inline text (e.g. templated labels, CreateSpaceDialog) legitimately uses. They are
// forced attribute-free — the `uponSanitizeAttribute` hook below drops EVERY attribute
// on them (global defaults like `title`/`lang`/`dir`, ARIA, everything), not just the
// class/style/id/`data-*`/`on*` axes DOMPurify already strips. With no attributes they
// bind no behavior and add no gadget/XSS surface.
//
// Two further candidates surfaced by the UI-service apply spike are deliberately
// EXCLUDED — and stay excluded — because a UGC preset is the wrong lever for them:
//   • block `pre` — `pre` is block-level, not inline. Inline preformatted text is
//     already served by `code` (allowed above); a site using `<pre>` for a single
//     line should emit inline `<code>` rather than widen this preset.
//   • hovercard `data-*` (`data-hovercard-type` / `-url`) — content that needs these
//     (e.g. commit-message links) is server-rendered, trust-verified HTML (`SafeHtml`),
//     which must render through @github-ui/safe-html's VerifiedHTML path (no runtime
//     sanitization) — NOT UnsafeHTML + a preset. Loosening a preset meant for untrusted
//     content to carry a behavior hook would weaken every other caller to serve content
//     that shouldn't be sanitized at all.
//
// CLASS POLICY — intentionally different from the `markdown` preset. `markdown`
// resolved its class axis to a `js-*`-only *denylist* so it can preserve the many
// legitimate classes rendered markdown carries (syntax highlighting, etc.). `inline`
// instead keeps memex's tight class *allowlist* (only `emoji` / `g-emoji`, and only on
// `img` / `g-emoji` tags). That is safe here precisely because the `ALLOWED_TAGS`
// override below already collapses the tag set to `a` / `img` / `g-emoji` / `code` /
// text — there are no rendered-markdown classes to preserve, so the allowlist
// neutralizes `js-*` behavior hooks with zero legitimate loss. Do NOT copy this class
// model to `markdown`, where the tag set does not collapse.

import type {Config, Purifier, UponSanitizeAttributeHookEvent} from '../types'
import {sanitizeNewWindowLinks, sanitizeNonGitHubImageLinks, SHARED_CONFIG} from './shared-hooks'

// Inline config — memex's TEXT baseline: an ALLOWED_TAGS override (NOT ADD_TAGS)
// that replaces DOMPurify's defaults, so only these inline tags are ever kept.
export const INLINE_SANITIZE_CONFIG: Readonly<Config> = {
  ...SHARED_CONFIG,

  // Using ALLOWED_TAGS (not ADD_TAGS) overrides DOMPurify's defaults, so ONLY the
  // tags listed here are considered safe — everything else is unwrapped.
  ALLOWED_TAGS: [
    // a: links inside titles / text custom fields
    'a',
    '#text',
    // img + g-emoji: GitHub custom emoji
    'img',
    'g-emoji',
    // code: inline code inside issue/PR titles
    'code',
    // Inert inline emphasis — presentational tags forced attribute-free by the
    // uponSanitizeAttribute hook below (a deliberate deviation from memex TEXT; see
    // the module header). No hovercard/data-* or block tags are added here — those
    // remain a later revisit.
    'strong',
    'em',
    'b',
    'i',
  ],
  ADD_ATTR: [
    // attributes for the g-emoji component
    'alias',
    'fallback-src',
  ],
  // NOTE: 'class' is intentionally NOT in FORBID_ATTR — the uponSanitizeAttribute
  // hook below must run to selectively allow it on the CLASS_ATTRIBUTE_ALLOWLIST tags.
  FORBID_ATTR: ['style', 'hidden', 'id'],
  // Keep the text content of stripped block tags rather than dropping it, so
  // flattening a block wrapper doesn't erase the inline text it contained.
  KEEP_CONTENT: true,
}

// ── Attribute hook — emphasis attribute stripping + `emoji`/`g-emoji` allowlist ──

// Class values kept on allowlisted tags. Restricting to these two prevents
// class-based XSS (e.g. `js-*` behavior hooks) — see github/memex#24146.
const ALLOWED_CLASS_NAMES = new Set(['emoji', 'g-emoji'])

// Tags permitted to carry a `class` at all. Only the tags that need it for
// GitHub custom-emoji rendering: `img` (class="emoji") and the `g-emoji` element.
const CLASS_ATTRIBUTE_ALLOWLIST = new Set(['g-emoji', 'img'])

// Inert inline emphasis tags. They are presentational only, so the hook forces them
// attribute-free: every attribute is dropped, matching the "attribute-free" contract
// documented in the module header. This closes global/ARIA attributes (`title`,
// `role`, `aria-*`, `lang`, `dir`, `translate`) that DOMPurify would otherwise keep.
const ATTRIBUTE_FREE_TAGS = new Set(['strong', 'em', 'b', 'i'])

/**
 * The inline preset's `uponSanitizeAttribute` hook. Two concerns:
 *   1. Emphasis tags (`strong`/`em`/`b`/`i`) are forced attribute-free — every
 *      attribute is dropped so these presentational tags carry no attributes at all.
 *   2. On the class-allowlisted tags (`img`/`g-emoji`), the `class` attribute is
 *      filtered to the `emoji`/`g-emoji` allowlist; on every other tag `class` is
 *      dropped. Ported from memex's `sanitizeClassAttribute` (github/memex#24146):
 *      a default-deny class allowlist rather than the markdown preset's `js-*`-only
 *      denylist. Class values are tokenized on ASCII whitespace (space, tab, LF, FF,
 *      CR) so obfuscated tokens can't smuggle a non-allowlisted class.
 */
const filterInlineAttribute = (node: Element, data: UponSanitizeAttributeHookEvent) => {
  const tagName = node.tagName.toLowerCase()

  // Emphasis tags: strip every attribute, unconditionally.
  if (ATTRIBUTE_FREE_TAGS.has(tagName)) {
    data.keepAttr = false
    return
  }

  if (data.attrName !== 'class') return

  if (!CLASS_ATTRIBUTE_ALLOWLIST.has(tagName)) {
    data.keepAttr = false
    return
  }

  const kept = data.attrValue.split(/[ \t\n\f\r]+/).filter(className => ALLOWED_CLASS_NAMES.has(className))
  if (kept.length === 0) {
    data.keepAttr = false
  } else {
    data.attrValue = kept.join(' ')
  }
}

/**
 * Attach the inline preset's security hooks to a purifier instance. Reuses the
 * shared link/image hardening and adds the inline-specific attribute filter
 * (emphasis attribute-stripping + class allowlist). Isomorphic: the image hook
 * guards on browser globals so the server (linkedom) path is a safe no-op for it.
 * Call from each entry's local `createPurifier`.
 */
export function addInlineSanitizeHooks(instance: Purifier): void {
  instance.addHook('afterSanitizeAttributes', sanitizeNewWindowLinks)
  instance.addHook('afterSanitizeElements', sanitizeNonGitHubImageLinks)
  instance.addHook('uponSanitizeAttribute', filterInlineAttribute)
}
